/**
 * Enhanced Project Card Component
 * Shows project information with comprehensive details
 */

import React from 'react';
import { motion } from 'framer-motion';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Link } from 'wouter';
import { Calendar, User, ExternalLink, Github, ArrowRight } from 'lucide-react';
import DualCompanyLogo from '@/components/ui/dual-company-logo';
import { companies, activities } from '../../../../db';
import { getCompanyDisplayInfo } from '@/lib/companyDisplayUtils';
import { createCompanySlug } from '@/lib/companyUtils';
import { encodeSkillName } from '@/lib/skills';
import { createActivitySlug } from '@/lib/activityUtils';
import { isMultiActivityProject, getProjectActivityIds, getPreviousActivityId, getNextActivityId, getActivityTitle } from '@/lib/projectUtils';
import { ProjectCardProps } from './types';

export const ProjectCard: React.FC<ProjectCardProps> = ({ 
  project, 
  showCompanyLogos,
  index 
}) => {
  
  const isPrivateProject = project.currentActivityId === null;
  const activity = project.currentActivityId ? activities.find(a => a.id === project.currentActivityId) : null;
  const displayInfo = activity ? getCompanyDisplayInfo(activity, companies) : null;
  const company = companies.find(c => c.name === project.currentCompanyName);

  // Collect all skills from categorized skills
  const allSkills: Array<{ name: string; weight: number; category: string }> = [];
  
  if (project.categorizedSkills) {
    Object.entries(project.categorizedSkills).forEach(([category, skills]) => {
      skills.forEach(skill => {
        allSkills.push({ ...skill, category });
      });
    });
  }

  // Sort skills by weight (highest first) and take top 8
  const topSkills = allSkills
    .sort((a, b) => b.weight - a.weight)
    .slice(0, 8);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.1 }}
    >
      <Card className="h-full hover:shadow-lg transition-shadow duration-300 group">
        <CardHeader className="space-y-3">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-3 flex-1 min-w-0">
              {showCompanyLogos && !isPrivateProject && displayInfo?.isDualCompany ? (
                <DualCompanyLogo
                  assignmentCompany={displayInfo.assignmentCompany}
                  tempAgency={displayInfo.tempAgency!}
                  size="sm"
                  className="flex-shrink-0"
                  onAssignmentCompanyClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    window.location.href = `/professional/${createCompanySlug(displayInfo.assignmentCompany.name)}`;
                  }}
                  onTempAgencyClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    window.location.href = `/professional/${createCompanySlug(displayInfo.tempAgency!.name)}`;
                  }}
                />
              ) : showCompanyLogos && company ? (
                <Avatar 
                  className="w-8 h-8 flex-shrink-0 cursor-pointer hover:scale-110 transition-transform"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    window.location.href = `/professional/${createCompanySlug(company.name)}`;
                  }}
                  title={`View ${company.name} details`}
                >
                  <AvatarImage src={company.logoPath || undefined} />
                  <AvatarFallback className="text-xs">
                    {company.logoText}
                  </AvatarFallback>
                </Avatar>
              ) : showCompanyLogos && isPrivateProject ? (
                <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <User className="w-4 h-4 text-primary" />
                </div>
              ) : null}
              
              <div className="flex-1 min-w-0">
                <CardTitle className="text-lg leading-tight group-hover:text-primary transition-colors">
                  <Link 
                    href={`/project/${project.title.toLowerCase().replace(/ /g, '-')}`}
                    className="hover:text-primary transition-colors cursor-pointer"
                  >
                    {project.title}
                  </Link>
                </CardTitle>
                <CardDescription className="flex items-center gap-2 mt-1">
                  <Calendar className="w-3 h-3" />
                  <span className="text-xs">
                    {new Date(project.startDate).toLocaleDateString()} - {
                      project.endDate && project.endDate !== 'Present' 
                        ? new Date(project.endDate).toLocaleDateString()
                        : 'Present'
                    }
                  </span>
                </CardDescription>
              </div>
            </div>
            
            <div className="flex items-center gap-1 flex-shrink-0">
              {(project.isMultiActivity || project.isContinued) && (
                <Badge variant="outline" className="text-xs">
                  {project.isMultiActivity ? 'Multi' : 'Cont.'}
                </Badge>
              )}
              {(!project.endDate || project.endDate === 'Present') && (
                <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
              )}
            </div>
          </div>
        </CardHeader>

        <CardContent className="space-y-4">
          {/* Activity Information */}
          {!isPrivateProject && (
            <div className="text-sm">
              {/* Check if this is a multi-activity/continued project */}
              {(() => {
                const projectActivityIds = getProjectActivityIds({ 
                  id: project.id, 
                  title: project.title, 
                  activityId: project.activityId, 
                  startDate: project.startDate, 
                  endDate: project.endDate, 
                  description: project.description 
                });
                const isMultiActivity = projectActivityIds.length > 1;
                const currentActivity = activities.find(a => a.id === project.currentActivityId);
                
                if (isMultiActivity && currentActivity) {
                  const previousActivityId = getPreviousActivityId({ 
                    id: project.id, 
                    title: project.title, 
                    activityId: project.activityId, 
                    startDate: project.startDate, 
                    endDate: project.endDate, 
                    description: project.description 
                  }, project.currentActivityId!);
                  const nextActivityId = getNextActivityId({ 
                    id: project.id, 
                    title: project.title, 
                    activityId: project.activityId, 
                    startDate: project.startDate, 
                    endDate: project.endDate, 
                    description: project.description 
                  }, project.currentActivityId!);
                  
                  const isProjectActive = !project.endDate || project.endDate === 'Present';
                  
                  return (
                    <div className="space-y-1">
                      {previousActivityId && (
                        <div className="text-muted-foreground">
                          <span className="text-xs font-medium">Started in:</span>{' '}
                          <Link href={`/activities/${createActivitySlug(getActivityTitle(previousActivityId))}`}>
                            <span className="text-primary hover:underline cursor-pointer">
                              {getActivityTitle(previousActivityId)}
                            </span>
                          </Link>
                        </div>
                      )}
                      <div className="text-foreground">
                        <span className="text-xs font-medium">
                          {previousActivityId ? 
                            (isProjectActive ? 'Continued in:' : 'Continued and finished in:') :
                            (isProjectActive ? 'Started in:' : 'Started and finished in:')
                          }
                        </span>{' '}
                        <Link href={`/activities/${createActivitySlug(currentActivity.title)}`}>
                          <span className="text-primary hover:underline cursor-pointer font-medium">
                            {currentActivity.title}
                          </span>
                        </Link>
                      </div>
                      {nextActivityId && (
                        <div className="text-muted-foreground">
                          <span className="text-xs font-medium">Continued in:</span>{' '}
                          <Link href={`/activities/${createActivitySlug(getActivityTitle(nextActivityId))}`}>
                            <span className="text-primary hover:underline cursor-pointer">
                              {getActivityTitle(nextActivityId)}
                            </span>
                          </Link>
                        </div>
                      )}
                    </div>
                  );
                } else if (currentActivity) {
                  // Single activity project
                  const isProjectActive = !project.endDate || project.endDate === 'Present';
                  
                  return (
                    <div>
                      <span className="text-xs font-medium text-muted-foreground">
                        {isProjectActive ? 'Started in:' : 'Started and finished in:'}
                      </span>{' '}
                      <Link href={`/activities/${createActivitySlug(currentActivity.title)}`}>
                        <span className="text-primary hover:underline cursor-pointer font-medium">
                          {currentActivity.title}
                        </span>
                      </Link>
                    </div>
                  );
                }
                return null;
              })()}
            </div>
          )}

          {/* Role Display */}
          {!isPrivateProject && project.role && (
            <div className="text-sm text-muted-foreground border border-border px-3 py-2 rounded bg-muted/30">
              <span className="text-xs font-medium">Role:</span> {project.role}
            </div>
          )}

          <p className="text-sm text-muted-foreground line-clamp-3">
            {project.description}
          </p>

          {/* Skills */}
          {topSkills.length > 0 && (
            <div className="space-y-2">
              <h4 className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                Key Skills
              </h4>
              <div className="flex flex-wrap gap-1">
                {topSkills.map((skill, index) => (
                  <Link 
                    key={`${skill.name}-${index}`}
                    href={`/professional/skill/${encodeSkillName(skill.name)}`}
                  >
                    <Badge 
                      variant="secondary" 
                      className="text-xs cursor-pointer hover:bg-primary/20 transition-colors"
                      title={`View ${skill.name} details - Proficiency: ${Math.round(skill.weight * 100)}%`}
                    >
                      {skill.name}
                    </Badge>
                  </Link>
                ))}
              </div>
            </div>
          )}

          {/* Metrics */}
          {project.metrics && project.metrics.length > 0 && (
            <div className="space-y-2">
              <h4 className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                Key Achievements
              </h4>
              <ul className="text-xs text-muted-foreground space-y-1">
                {project.metrics.slice(0, 3).map((metric, index) => (
                  <li key={index} className="flex items-start gap-2">
                    <ArrowRight className="w-3 h-3 mt-0.5 flex-shrink-0 text-primary" />
                    <span>{metric}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Actions */}
          <div className="flex items-center justify-between pt-2">
            <div className="flex items-center gap-2">
              {project.git && project.git !== 'not available' && !project.git.includes('inner source') && (
                <Button size="sm" variant="outline" asChild>
                  <a href={project.git} target="_blank" rel="noopener noreferrer">
                    <Github className="w-3 h-3" />
                  </a>
                </Button>
              )}
              {project.link && project.link !== 'not available' && project.link !== project.git && !project.link.includes('inner source') && (
                <Button size="sm" variant="outline" asChild>
                  <a href={project.link} target="_blank" rel="noopener noreferrer">
                    <ExternalLink className="w-3 h-3" />
                  </a>
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
};